/**
 * 🎨 UI Components - Reusable styled components using the centralized theme
 * Import and use these components throughout the app for consistent styling
 */

import React from 'react';
import { components, logo } from '../../styles/theme.js';
import { cn } from '../../utils/themeUtils.js';
import { theme } from '../../styles/theme';

// Button Component
export const Button = ({
  variant = 'primary',
  size = 'md',
  className = '',
  disabled = false,
  children,
  ...props
}) => {
  const sizeClasses = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-base',
    lg: 'px-6 py-3 text-lg',
  };

  const variantClasses = {
    primary: disabled
      ? 'bg-white/70 text-black/70 cursor-not-allowed shadow-lg'
      : components.button.primary,
    secondary: components.button.secondary,
    danger: components.button.danger,
    ghost: components.button.ghost,
  };

  const baseClasses = `${components.button.base} ${sizeClasses[size]} ${variantClasses[variant]}`;

  return (
    <button
      className={`${baseClasses} ${className}`}
      disabled={disabled}
      {...props}
    >
      {children}
    </button>
  );
};

// Card Component
export const Card = ({
  hover = false,
  shadow = true,
  className = '',
  children,
  ...props
}) => {
  const cardClasses = `
    ${components.card.base}
    ${hover ? components.card.hover : ''}
    ${shadow ? components.card.shadow : ''}
    ${hover && shadow ? components.card.shadowHover : ''}
  `.trim();

  return (
    <div className={`${cardClasses} ${className}`} {...props}>
      {children}
    </div>
  );
};

// Input Component
export const Input = ({
  error = false,
  className = '',
  ...props
}) => {
  const inputClasses = `
    ${components.input.base}
    ${error ? components.input.error : components.input.focus}
  `.trim();

  return (
    <input
      className={`${inputClasses} ${className}`}
      {...props}
    />
  );
};

// Textarea Component
export const Textarea = ({
  error = false,
  className = '',
  ...props
}) => {
  const inputClasses = `
    ${components.input.base}
    ${error ? components.input.error : components.input.focus}
    resize-none
  `.trim();

  return (
    <textarea
      className={`${inputClasses} ${className}`}
      {...props}
    />
  );
};

// Text Components
export const Text = ({
  variant = 'primary',
  size = 'base',
  weight = 'normal',
  className = '',
  children,
  as: Component = 'p',
  ...props
}) => {
  const variantClasses = components.text[variant] || components.text.primary;

  const sizeClasses = {
    xs: 'text-xs',
    sm: 'text-sm',
    base: 'text-base',
    lg: 'text-lg',
    xl: 'text-xl',
    '2xl': 'text-2xl',
    '3xl': 'text-3xl',
    '4xl': 'text-4xl',
    '5xl': 'text-5xl',
  };

  const weightClasses = {
    normal: 'font-normal',
    medium: 'font-medium',
    semibold: 'font-semibold',
    bold: 'font-bold',
  };

  return (
    <Component
      className={`${variantClasses} ${sizeClasses[size]} ${weightClasses[weight]} ${className}`}
      {...props}
    >
      {children}
    </Component>
  );
};

// Heading Components
export const Heading = ({
  level = 1,
  variant = 'primary',
  className = '',
  children,
  ...props
}) => {
  const Component = `h${level}`;
  const variantClasses = components.text[variant] || components.text.primary;

  const sizeClasses = {
    1: 'text-4xl md:text-5xl font-bold',
    2: 'text-3xl md:text-4xl font-bold',
    3: 'text-2xl md:text-3xl font-semibold',
    4: 'text-xl md:text-2xl font-semibold',
    5: 'text-lg md:text-xl font-medium',
    6: 'text-base md:text-lg font-medium',
  };

  return (
    <Component
      className={`${variantClasses} ${sizeClasses[level]} ${className}`}
      {...props}
    >
      {children}
    </Component>
  );
};

// Modal Component
export const Modal = ({
  isOpen,
  onClose,
  size = 'md',
  className = '',
  children,
  ...props
}) => {
  if (!isOpen) return null;

  const sizeClasses = {
    sm: 'w-full max-w-sm',
    md: 'w-full max-w-md',
    lg: 'w-full max-w-lg',
    xl: 'w-full max-w-xl',
    '2xl': 'w-full max-w-2xl',
  };

  return (
    <div className={components.modal.backdrop} onClick={onClose}>
      <div
        className={`${components.modal.container} ${sizeClasses[size]} mx-4 p-6 ${className}`}
        onClick={(e) => e.stopPropagation()}
        {...props}
      >
        {children}
      </div>
    </div>
  );
};

// Badge/Status Component
export const Badge = ({
  variant = 'info',
  size = 'sm',
  className = '',
  children,
  ...props
}) => {
  const variantClasses = components.status[variant] || components.status.info;

  const sizeClasses = {
    xs: 'text-xs px-1.5 py-0.5',
    sm: 'text-xs px-2 py-1',
    md: 'text-sm px-2.5 py-1.5',
  };

  return (
    <span
      className={`${variantClasses} ${sizeClasses[size]} rounded-full font-medium ${className}`}
      {...props}
    >
      {children}
    </span>
  );
};

// Container Component
export const Container = ({
  size = '6xl',
  className = '',
  children,
  ...props
}) => {
  const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    '2xl': 'max-w-2xl',
    '4xl': 'max-w-4xl',
    '6xl': 'max-w-6xl',
    '7xl': 'max-w-7xl',
  };

  return (
    <div
      className={`${sizeClasses[size]} mx-auto px-4 sm:px-6 lg:px-8 ${className}`}
      {...props}
    >
      {children}
    </div>
  );
};

// Loading Spinner Component
export const Spinner = ({
  size = 'md',
  color = 'accent',
  className = '',
  ...props
}) => {
  const sizeClasses = {
    xs: 'h-3 w-3',
    sm: 'h-4 w-4',
    md: 'h-6 w-6',
    lg: 'h-8 w-8',
    xl: 'h-12 w-12',
  };

  const colorClasses = {
    accent: 'border-[#7B61FF]',
    white: 'border-white',
    gray: 'border-gray-300',
  };

  return (
    <div
      className={`animate-spin rounded-full border-b-2 ${sizeClasses[size]} ${colorClasses[color]} ${className}`}
      {...props}
    />
  );
};

// Alert/Notification Component
export const Alert = ({
  variant = 'info',
  className = '',
  children,
  onClose,
  ...props
}) => {
  const variantClasses = {
    success: 'bg-green-400/10 border-green-400/30 text-green-400',
    warning: 'bg-yellow-400/10 border-yellow-400/30 text-yellow-400',
    error: 'bg-red-400/10 border-red-400/30 text-red-400',
    info: 'bg-[#7B61FF]/10 border-[#7B61FF]/30 text-[#BA87FF]',
  };

  return (
    <div
      className={`border rounded-lg p-3 ${variantClasses[variant]} ${className}`}
      {...props}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">{children}</div>
        {onClose && (
          <button
            onClick={onClose}
            className="ml-2 opacity-70 hover:opacity-100 transition-opacity"
          >
            ✕
          </button>
        )}
      </div>
    </div>
  );
};

// Navigation Link Component
export const NavLink = ({
  active = false,
  className = '',
  children,
  ...props
}) => {
  const navClasses = active ? components.nav.active : components.nav.inactive;
  const Component = props.onClick ? 'button' : 'a';

  return (
    <Component
      className={`${navClasses} ${className}`}
      {...props}
    >
      {children}
    </Component>
  );
};

// Form Group Component
export const FormGroup = ({
  label,
  error,
  required = false,
  className = '',
  children,
  ...props
}) => {
  return (
    <div className={`space-y-1 ${className}`} {...props}>
      {label && (
        <label className={`block text-sm font-medium ${components.text.primary}`}>
          {label}
          {required && <span className="text-red-400 ml-1">*</span>}
        </label>
      )}
      {children}
      {error && (
        <p className={`text-sm ${components.text.error}`}>
          {error}
        </p>
      )}
    </div>
  );
};

// Checkbox Component
export const Checkbox = ({
  label,
  className = '',
  ...props
}) => {
  return (
    <label className="flex items-center">
      <input
        type="checkbox"
        className={`h-4 w-4 text-[#7B61FF] focus:ring-[#7B61FF] bg-[#2A2D36] border-[#3A3D46] rounded ${className}`}
        {...props}
      />
      {label && <span className="ml-3 text-sm text-[#CCCCCC]">{label}</span>}
    </label>
  );
};

// Radio Component
export const Radio = ({
  label,
  className = '',
  ...props
}) => {
  return (
    <label className="flex items-center">
      <input
        type="radio"
        className={`h-4 w-4 text-[#7B61FF] focus:ring-[#7B61FF] bg-[#2A2D36] border-[#3A3D46] ${className}`}
        {...props}
      />
      {label && <span className="ml-3 text-sm text-[#CCCCCC]">{label}</span>}
    </label>
  );
};

// Logo Text Component - "The [LOGO]rchitect"
export const LogoText = ({
  size = 'md',
  className = '',
  onClick,
  ...props
}) => {
  const sizeClasses = {
    sm: {
      text: 'text-lg',
      logo: 'h-8 w-8',
    },
    md: {
      text: 'text-xl',
      logo: 'h-10 w-10',
    },
    lg: {
      text: 'text-2xl',
      logo: 'h-14 w-14',
    },
    xl: {
      text: 'text-3xl',
      logo: 'h-16 w-16',
    },
  };

  const currentSize = sizeClasses[size] || sizeClasses.md;

  return (
    <div
      className={`flex items-center ${className} ${onClick ? 'cursor-pointer' : ''}`}
      onClick={onClick}
      {...props}
    >
      <span className={`font-bold text-white ${currentSize.text}`}>
        The{' '}
      </span>
      <img
        src={logo.paths.main}
        alt="A"
        className={`${currentSize.logo} ml-0.5 -mr-2.5`}
      />
      <span className={`font-bold text-white ${currentSize.text}`}>
        rchitect
      </span>
    </div>
  );
};

// Logo Component
export const Logo = ({
  variant = 'main',
  size = 'md',
  className = '',
  onClick,
  ...props
}) => {
  // Get logo path from theme configuration
  const logoSrc = logo.paths[variant] || logo.paths.main;

  // Get size classes from theme configuration
  const sizeClass = logo.sizes[size] || logo.sizes.md;

  return (
    <img
      src={logoSrc}
      alt={logo.alt}
      className={`${sizeClass} ${className} ${onClick ? 'cursor-pointer' : ''}`}
      onClick={onClick}
      {...props}
    />
  );
};

// MetricCard Component - For professional dashboard-style metrics
export const MetricCard = ({
  value,
  label,
  valueColor = 'text-white',
  icon = null,
  trend = null,
  className = '',
  children,
  ...props
}) => {
  return (
    <div className={cn(theme.presets.metricCard.container, className)} {...props}>
      {children ? (
        children
      ) : (
        <>
          <div className="flex items-center justify-between mb-2">
            <Text variant="secondary" size="sm">{label}</Text>
            {icon && <div className="text-[#CCCCCC]">{icon}</div>}
          </div>
          <div className="flex items-center justify-between">
            <Text size="2xl" weight="bold" className={valueColor}>{value}</Text>
            {trend && (
              <div className={`text-sm ${trend.positive ? 'text-green-400' : 'text-red-400'}`}>
                {trend.value}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

// MetricGrid Component - For consistent grid layouts
export const MetricGrid = ({
  columns = 4,
  children,
  className = '',
  ...props
}) => {
  const gridClass = theme.layout.grid[`metrics${columns}`] || theme.layout.grid.metrics4;
  return (
    <div className={cn(gridClass, className)} {...props}>
      {children}
    </div>
  );
};

// InternalSection Component - For sections within cards using input color
export const InternalSection = ({
  children,
  variant = 'section',
  className = '',
  ...props
}) => {
  const variantClass = theme.presets.internal[variant] || theme.presets.internal.section;
  return (
    <div className={cn(variantClass, className)} {...props}>
      {children}
    </div>
  );
};

// FeatureGrid Component - For feature displays
export const FeatureGrid = ({
  children,
  className = '',
  ...props
}) => {
  return (
    <div className={cn(theme.presets.features.grid, className)} {...props}>
      {children}
    </div>
  );
};

// FeatureCard Component - For feature displays
export const FeatureCard = ({
  children,
  className = '',
  ...props
}) => {
  return (
    <div className={cn(theme.presets.features.card, className)} {...props}>
      {children}
    </div>
  );
};

// Icon Component - Clean minimal white icons
export const Icon = ({
  name,
  size = 'md',
  className = '',
  ...props
}) => {
  const sizeClasses = {
    xs: 'h-3 w-3',
    sm: 'h-4 w-4',
    md: 'h-5 w-5',
    lg: 'h-6 w-6',
    xl: 'h-8 w-8',
    '2xl': 'h-12 w-12',
  };

  const icons = {
    menu: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M4 6h16" />
        <path d="M4 12h16" />
        <path d="M4 18h16" />
      </svg>
    ),
    // Security & Access
    search: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="11" cy="11" r="8" />
        <path d="m21 21-4.35-4.35" />
      </svg>
    ),

    shield: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10" />
      </svg>
    ),

    lock: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
      </svg>
    ),

    // Analytics & Data
    chart: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M3 3v18h18" />
        <path d="m19 9-5 5-4-4-3 3" />
      </svg>
    ),

    // Financial
    creditCard: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect width="20" height="14" x="2" y="5" rx="2" />
        <line x1="2" x2="22" y1="10" y2="10" />
      </svg>
    ),

    shoppingCart: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="8" cy="21" r="1" />
        <circle cx="19" cy="21" r="1" />
        <path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57L20.05 9H5.12" />
      </svg>
    ),

    money: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <line x1="12" x2="12" y1="2" y2="22" />
        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
      </svg>
    ),

    download: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
        <polyline points="7,10 12,15 17,10" />
        <line x1="12" x2="12" y1="15" y2="3" />
      </svg>
    ),

    heart: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
      </svg>
    ),

    diamond: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M6 3h12l4 6-10 13L2 9l4-6z" />
      </svg>
    ),

    star: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
      </svg>
    ),

    gift: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="8" width="18" height="4" rx="1" />
        <path d="M12 8v13" />
        <path d="M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7" />
        <path d="M7.5 8a2.5 2.5 0 0 1 0-5A4.9 4.9 0 0 1 12 8a4.9 4.9 0 0 1 4.5-5 2.5 2.5 0 0 1 0 5" />
      </svg>
    ),

    // Documents & Content
    document: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
        <polyline points="14,2 14,8 20,8" />
        <line x1="16" x2="8" y1="13" y2="13" />
        <line x1="16" x2="8" y1="17" y2="17" />
        <line x1="10" x2="8" y1="9" y2="9" />
      </svg>
    ),

    clipboard: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect width="8" height="4" x="8" y="2" rx="1" />
        <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
      </svg>
    ),


    // Actions & Status  
    check: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M20 6 9 17l-5-5" />
      </svg>
    ),

    x: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M18 6 6 18" />
        <path d="M6 6l12 12" />
      </svg>
    ),

    warning: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="m21.73 18-8-14a2 2 0 0 0-3.46 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
        <path d="M12 9v4" />
        <path d="M12 17h.01" />
      </svg>
    ),

    // Time & Calendar
    calendar: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M8 2v4" />
        <path d="M16 2v4" />
        <rect width="18" height="18" x="3" y="4" rx="2" />
        <path d="M3 10h18" />
      </svg>
    ),

    clock: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10" />
        <polyline points="12,6 12,12 16,14" />
      </svg>
    ),

    // Movement & Direction
    arrowUp: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m18 15-6-6-6 6" /></svg>,
    arrowLeft: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m15 18-6-6 6-6" /></svg>,
    arrowRight: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m9 18 6-6-6-6" /></svg>,
    refresh: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" /><path d="M21 3v5h-5" /><path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" /><path d="M3 21v-5h5" /></svg>,
    success: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 12l2 2 4-4" /><circle cx="12" cy="12" r="10" /></svg>,

    // Technology & Tools
    phone: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect width="14" height="20" x="5" y="2" rx="2" ry="2" />
        <path d="M12 18h.01" />
      </svg>
    ),

    rocket: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z" />
        <path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z" />
        <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0" />
        <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5" />
      </svg>
    ),

    brain: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M9.5 2A2.5 2.5 0 0 1 12 4.5v15a2.5 2.5 0 0 1-4.96.44 2.5 2.5 0 0 1-2.96-3.08 3 3 0 0 1-.34-5.58 2.5 2.5 0 0 1 1.32-4.24 2.5 2.5 0 0 1 1.98-3A2.5 2.5 0 0 1 9.5 2Z" />
        <path d="M14.5 2A2.5 2.5 0 0 0 12 4.5v15a2.5 2.5 0 0 0 4.96.44 2.5 2.5 0 0 0 2.96-3.08 3 3 0 0 0 .34-5.58 2.5 2.5 0 0 0-1.32-4.24 2.5 2.5 0 0 0-1.98-3A2.5 2.5 0 0 0 14.5 2Z" />
      </svg>
    ),

    cursor: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M3 3l18 9-8 2-2 8z" />
        <path d="M11 13l2-2" />
      </svg>
    ),

    lightbulb: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 21h6" /><path d="M12 17h0" /><path d="M12 3s3 3 3 7.5-1.5 6.5-3 6.5-3-2-3-6.5 3-7.5 3-7.5" /></svg>,
    lightning: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="13,2 3,14 12,14 11,22 21,10 12,10 13,2" /></svg>,
    folder: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" /></svg>,
    layout: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect width="18" height="18" x="3" y="3" rx="2" /><path d="M3 9h18" /><path d="M9 21V9" /></svg>,
    server: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect width="20" height="8" x="2" y="2" rx="2" ry="2" /><rect width="20" height="8" x="2" y="14" rx="2" ry="2" /><line x1="6" x2="6.01" y1="6" y2="6" /><line x1="6" x2="6.01" y1="18" y2="18" /></svg>,
    cloud: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z" /></svg>,

    target: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="6" /><circle cx="12" cy="12" r="2" /></svg>,

    home: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9,22 9,12 15,12 15,22" /></svg>,

    question: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" /><path d="M12 17h.01" /></svg>,

    user: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>,

    // Interface & Navigation  
    settings: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.38a2 2 0 0 0-.73-2.73l-.15-.09a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.39a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
        <circle cx="12" cy="12" r="3" />
      </svg>
    ),

    dashboard: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect width="7" height="9" x="3" y="3" rx="1" />
        <rect width="7" height="5" x="14" y="3" rx="1" />
        <rect width="7" height="9" x="14" y="12" rx="1" />
        <rect width="7" height="5" x="3" y="16" rx="1" />
      </svg>
    ),

    database: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <ellipse cx="12" cy="5" rx="9" ry="3" />
        <path d="M3 5v14c0 1.66 4.03 3 9 3s9-1.34 9-3V5" />
        <path d="M3 12c0 1.66 4.03 3 9 3s9-1.34 9-3" />
      </svg>
    ),

    layers: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 2 3 7l9 5 9-5-9-5z" />
        <path d="M3 12l9 5 9-5" />
        <path d="M3 17l9 5 9-5" />
      </svg>
    ),

    plus: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M5 12h14" />
        <path d="M12 5v14" />
      </svg>
    ),

    // Business Actions
    trendingUp: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polyline points="22,7 13.5,15.5 8.5,10.5 2,17" />
        <polyline points="16,7 22,7 22,13" />
      </svg>
    ),

    // User Actions
    signOut: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
        <polyline points="10,17 15,12 10,7" />
        <path d="M15 12H3" />
      </svg>
    ),

    eye: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
      </svg>
    ),

    mail: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect width="20" height="16" x="2" y="4" rx="2" />
        <path d="m2 7 10 6 10-6" />
      </svg>
    ),

    // Sharing & Clipboard
    share: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="18" cy="5" r="3" />
        <circle cx="6" cy="12" r="3" />
        <circle cx="18" cy="19" r="3" />
        <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
        <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
      </svg>
    ),

    copy: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
      </svg>
    ),

    link: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
      </svg>
    ),

    // Added for HowItWorks Tour
    smartphone: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect width="14" height="20" x="5" y="2" rx="2" ry="2" />
        <path d="M12 18h.01" />
      </svg>
    ),
    wifiOff: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <line x1="1" x2="23" y1="1" y2="23" />
        <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55" />
        <path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39" />
        <path d="M10.71 5.05A16 16 0 0 1 22.58 9" />
        <path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88" />
        <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
        <line x1="12" x2="12.01" y1="20" y2="20" />
      </svg>
    ),
    fileText: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
        <polyline points="14,2 14,8 20,8" />
        <line x1="16" x2="8" y1="13" y2="13" />
        <line x1="16" x2="8" y1="17" y2="17" />
        <line x1="10" x2="8" y1="9" y2="9" />
      </svg>
    ),
    cpu: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="4" y="4" width="16" height="16" rx="2" ry="2" />
        <rect x="9" y="9" width="6" height="6" />
        <line x1="9" x2="9" y1="1" y2="4" />
        <line x1="15" x2="15" y1="1" y2="4" />
        <line x1="9" x2="9" y1="20" y2="23" />
        <line x1="15" x2="15" y1="20" y2="23" />
        <line x1="20" x2="23" y1="9" y2="9" />
        <line x1="20" x2="23" y1="15" y2="15" />
        <line x1="1" x2="4" y1="9" y2="9" />
        <line x1="1" x2="4" y1="15" y2="15" />
      </svg>
    ),
    checkCircle: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
        <polyline points="22 4 12 14.01 9 11.01" />
      </svg>
    ),
    code: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polyline points="16 18 22 12 16 6" />
        <polyline points="8 6 2 12 8 18" />
      </svg>
    ),
    alertTriangle: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="m21.73 18-8-14a2 2 0 0 0-3.46 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
        <line x1="12" x2="12" y1="9" y2="13" />
        <line x1="12" x2="12.01" y1="17" y2="17" />
      </svg>
    ),

    // Device icons for Security tab
    monitor: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect width="20" height="14" x="2" y="3" rx="2" />
        <line x1="8" x2="16" y1="21" y2="21" />
        <line x1="12" x2="12" y1="17" y2="21" />
      </svg>
    ),

    tablet: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect width="16" height="20" x="4" y="2" rx="2" ry="2" />
        <line x1="12" x2="12.01" y1="18" y2="18" />
      </svg>
    ),

    globe: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10" />
        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
        <path d="M2 12h20" />
      </svg>
    ),

    alertCircle: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10" />
        <line x1="12" x2="12" y1="8" y2="12" />
        <line x1="12" x2="12.01" y1="16" y2="16" />
      </svg>
    ),

    info: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10" />
        <line x1="12" x2="12" y1="16" y2="12" />
        <line x1="12" x2="12.01" y1="8" y2="8" />
      </svg>
    ),
  };

  const IconSvg = icons[name];

  if (!IconSvg) {
    return null;
  }

  const iconElement = React.cloneElement(IconSvg, {
    className: `${sizeClasses[size]}`,
    style: { color: 'currentColor', fill: 'none', stroke: 'currentColor' }
  });

  return (
    <span className={`inline-flex items-center justify-center ${sizeClasses[size]} text-white ${className}`} {...props}>
      {iconElement}
    </span >
  );
};
// Mobile Bottom Nav Component
export { MobileBottomNav } from './MobileBottomNav.jsx';
